import React, { useState } from "react";
import { searchStocks } from "../lib/krStocks";
import { pairLegs } from "../lib/pairs";

const sel = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #e2e2e2", fontSize: 13, boxSizing: "border-box" };
const label = { fontSize: 12, color: "#888", display: "block", marginBottom: 6 };
const MKT_LAB = { KS: "코스피", KQ: "코스닥", KR: "미분류" };

// 마스터 + 보유종목 통합 검색
function searchAll(q, holdings) {
  const master = searchStocks(q, 8);
  const seen = new Set(master.map((m) => m.c));
  const s = String(q).toLowerCase();
  const held = (holdings || [])
    .filter((h) => (String(h.name || "").toLowerCase().includes(s) || String(h.ticker).includes(s)))
    .map((h) => ({ n: h.name || String(h.ticker), c: String(h.ticker).replace(/\D/g, "").padStart(6, "0"), m: (h.market === "KS" || h.market === "KQ") ? h.market : "KR" }))
    .filter((m) => !seen.has(m.c));
  return [...master, ...held].slice(0, 8);
}

// 종목명 검색해서 레그 추가
function LegAdder({ holdings, onAdd }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const matches = open && q ? searchAll(q, holdings) : [];
  return (
    <div style={{ position: "relative" }}>
      <input style={{ ...sel, borderStyle: "dashed" }} placeholder="+ 종목 검색해서 추가"
        value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {matches.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, marginTop: 3, maxHeight: 220, overflowY: "auto", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }}>
          {matches.map((m) => (
            <div key={m.c} onMouseDown={() => { onAdd(m); setQ(""); setOpen(false); }}
              style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, display: "flex", justifyContent: "space-between" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f2f5fb")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}>
              <span><b>{m.n}</b> <span style={{ color: "#aaa" }}>{m.c}</span></span>
              <span style={{ color: "#bbb", fontSize: 11 }}>{MKT_LAB[m.m]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AddPairModal({ holdings, onAdd, onClose, initial }) {
  const init = pairLegs(initial);
  const [longs, setLongs] = useState(init.longs.map((l) => ({ n: l.name, c: l.ticker, m: l.market })));
  const [shorts, setShorts] = useState(init.shorts.map((l) => ({ n: l.name, c: l.ticker, m: l.market })));
  const editing = !!(initial && (init.longs.length || init.shorts.length));

  const setSide = (side) => (side === "long" ? setLongs : setShorts);
  const addLeg = (side, leg) => setSide(side)((prev) => (prev.some((x) => x.c === leg.c) ? prev : [...prev, leg]));
  const rmLeg = (side, c) => setSide(side)((prev) => prev.filter((x) => x.c !== c));
  const setMkt = (side, c, m) => setSide(side)((prev) => prev.map((x) => (x.c === c ? { ...x, m } : x)));

  const submit = () => {
    if (!longs.length || !shorts.length) return;
    onAdd({
      longs: longs.map((l) => ({ ticker: l.c, name: l.n, market: l.m })),
      shorts: shorts.map((l) => ({ ticker: l.c, name: l.n, market: l.m })),
    });
    onClose();
  };

  const legList = (side, list, color) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {list.map((leg) => (
        <div key={leg.c} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 8, background: "#f7f8fa", border: "1px solid #eee" }}>
          <span style={{ flex: 1, fontSize: 13 }}><b style={{ color }}>{leg.n}</b> <span style={{ color: "#aaa" }}>{leg.c}</span></span>
          <select value={leg.m} onChange={(e) => setMkt(side, leg.c, e.target.value)} style={{ ...sel, width: 84, padding: "5px 8px" }} title="시장">
            <option value="KS">코스피</option>
            <option value="KQ">코스닥</option>
          </select>
          <button onClick={() => rmLeg(side, leg.c)} style={{ border: "none", background: "none", color: "#bbb", cursor: "pointer", fontSize: 15 }}>✕</button>
        </div>
      ))}
      <LegAdder holdings={holdings} onAdd={(leg) => addLeg(side, leg)} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "min(500px, 95vw)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>스프레드 페어 {editing ? "수정" : "추가"}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#aaa" }}>✕</button>
        </div>
        <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 18 }}>
          <div>
            <label style={label}>🔺 롱 레그 <span style={{ color: "#bbb" }}>({longs.length})</span></label>
            {legList("long", longs, "#0a7c59")}
          </div>
          <div>
            <label style={label}>🔻 숏 레그 <span style={{ color: "#bbb" }}>({shorts.length})</span></label>
            {legList("short", shorts, "#c0392b")}
          </div>
          <button onClick={submit} disabled={!longs.length || !shorts.length}
            style={{ width: "100%", padding: 11, borderRadius: 8, border: "none", background: (longs.length && shorts.length) ? "#1f3a93" : "#ccc", color: "#fff", fontSize: 14, fontWeight: 700, cursor: (longs.length && shorts.length) ? "pointer" : "default" }}>
            페어 {editing ? "수정" : "추가"}
          </button>
          <div style={{ fontSize: 11, color: "#aaa" }}>
            롱·숏 각각 여러 종목(바스켓) 가능. 스프레드 = ln(롱바스켓) − β·ln(숏바스켓), 바스켓=레그 기하평균.
          </div>
        </div>
      </div>
    </div>
  );
}
